import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, BellRing, Bell } from "lucide-react";
import { api } from "../lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const todayShort = new Date().toLocaleDateString("en-US", { weekday: "short" });

export default function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [title, setTitle] = useState("");
  const [time, setTime] = useState("18:00");
  const [days, setDays] = useState(["Mon", "Wed", "Fri"]);

  const load = async () => setReminders((await api.get("/reminders")).data);
  useEffect(() => { load(); }, []);

  const toggleDay = (d) => setDays(days.includes(d) ? days.filter((x) => x !== d) : [...days, d]);

  const add = async (e) => {
    e.preventDefault();
    if (!title) return;
    await api.post("/reminders", { title, time, days, active: true });
    toast.success("Reminder set");
    setTitle(""); load();
  };

  const toggleActive = async (r) => { await api.patch(`/reminders/${r.id}`, { active: !r.active }); load(); };
  const remove = async (id) => { await api.delete(`/reminders/${id}`); load(); };

  const dueToday = reminders.filter((r) => r.active && r.days.includes(todayShort));

  return (
    <div className="p-6 md:p-10 max-w-4xl">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF9500] mb-2">Stay Accountable</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-none">Reminders</h1>
      </div>

      {dueToday.length > 0 && (
        <div className="mb-6 p-5 border border-[#FF9500]/40 bg-[#FF9500]/10 flex items-center gap-4 fade-up" data-testid="due-today-banner">
          <BellRing className="text-[#FF9500] shrink-0" size={26} />
          <div>
            <div className="font-display font-bold text-lg uppercase tracking-tight">You've got training today</div>
            <div className="text-white/70 text-sm">{dueToday.map((r) => `${r.title} at ${r.time}`).join(" · ")}</div>
          </div>
        </div>
      )}

      <form onSubmit={add} className="p-6 bg-[#121212] border border-white/10 mb-6 space-y-4">
        <h3 className="font-display font-bold text-lg uppercase tracking-tight">New Reminder</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Title</label>
            <input data-testid="reminder-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Leg Day" className="mt-2 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2.5 focus:border-[#FF9500] focus:outline-none" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Time</label>
            <input data-testid="reminder-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-2 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2.5 focus:border-[#FF9500] focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Days</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {DAYS.map((d) => (
              <button type="button" key={d} data-testid={`day-${d}`} onClick={() => toggleDay(d)} className={`px-3 py-2 text-xs font-bold uppercase tracking-wide border transition-colors ${days.includes(d) ? "bg-[#FF9500] border-[#FF9500] text-black" : "bg-[#0A0A0A] border-white/10 text-white/50 hover:border-white/30"}`}>{d}</button>
            ))}
          </div>
        </div>
        <button data-testid="save-reminder" className="bg-[#FF9500] hover:bg-[#ffa733] text-black font-bold uppercase tracking-wide px-6 py-3 flex items-center gap-2 transition-transform duration-150 hover:-translate-y-0.5">
          <Plus size={18} /> Set Reminder
        </button>
      </form>

      {reminders.length === 0 ? (
        <div className="border border-dashed border-white/10 py-20 text-center text-white/40">
          <Bell size={36} className="mx-auto mb-3 text-white/20" /> No reminders yet.
        </div>
      ) : (
        <div className="space-y-3" data-testid="reminder-list">
          {reminders.map((r) => (
            <div key={r.id} className={`p-5 bg-[#121212] border transition-colors ${r.active ? "border-white/10" : "border-white/5 opacity-50"}`}>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-display font-bold text-xl uppercase tracking-tight">{r.title}</h3>
                    <span className="text-[#FF9500] font-bold tabular-nums">{r.time}</span>
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    {DAYS.map((d) => (
                      <span key={d} className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${r.days.includes(d) ? "bg-[#FF9500]/20 text-[#FF9500]" : "text-white/20"}`}>{d}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button data-testid={`toggle-reminder-${r.id}`} onClick={() => toggleActive(r)} className={`relative w-11 h-6 transition-colors ${r.active ? "bg-[#FF9500]" : "bg-white/15"}`}>
                    <span className={`absolute top-0.5 h-5 w-5 bg-white transition-transform ${r.active ? "translate-x-5" : "translate-x-0.5"}`} />
                  </button>
                  <button data-testid={`delete-reminder-${r.id}`} onClick={() => remove(r.id)} className="text-white/30 hover:text-[#FF3B30]"><Trash2 size={18} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
